<template>
  <div>
    <b-modal id="raiseIssueModal" v-model="modalVisible" centered hide-footer no-close-on-backdrop hide-header-close
      modal-class="iha-modal">
      <template #modal-header>
        <div class="view-clients-header">
          <b-button variant="close-btn" class="btn-container" @click="closeModal">
            <i class="fas fa-times"></i>
          </b-button>
        </div>
      </template>
      <div class="issue-modal-card">
        <h5 class="modal-title mb-3">Submit an Issue</h5>

        <div class="form-group mb-3 position-relative">
          <label class="mb-2">Issue Type <span class="text-danger">*</span></label>
          <div class="position-relative">
            <select class="form-control custom-select" v-model="status" @change="markError = ''">
              <option value="">Select Type</option>
              <option v-for="item in issueTypes" :key="item" :value="item">
                {{ item }}
              </option>
            </select>
            <i class="fas fa-angle-down dropdown-icon"></i>
          </div>
          <small v-if="markError" class="field-error">{{ markError }}</small>
        </div>

        <div class="form-group mb-3 position-relative">
          <label class="mb-2">Member <span class="text-danger">*</span></label>
          <div class="position-relative">
            <multiselect v-if="modalVisible" v-model="member" :options="members" :custom-label="getMemberLabel" :loading="isLoadingMembers"
              @search-change="onMemberSearch" :internal-search="false" :clear-on-select="true" :close-on-select="true"
              :options-limit="50" :limit="10" track-by="member_id" placeholder="Select Member" @input="memberError = ''">
              <template slot="noResult">No members found. Consider changing the search query.</template>
            </multiselect>
          </div>
          <small v-if="memberError" class="field-error">{{ memberError }}</small>
        </div>

        <div class="form-group mb-3 position-relative">
          <label class="mb-2">Plan <span class="text-danger">*</span></label>
          <div class="position-relative">
            <multiselect v-if="modalVisible" v-model="plan" :options="plans" :custom-label="getPlanLabel" :loading="isLoadingPlans"
              @search-change="onPlanSearch" :internal-search="false" :clear-on-select="true" :close-on-select="true"
              :options-limit="50" :limit="10" track-by="pid" placeholder="Select Plan" @input="planError = ''">
              <template slot="noResult">No plans found. Consider changing the search query.</template>
            </multiselect>
          </div>
          <small v-if="planError" class="field-error">{{ planError }}</small>
        </div>

        <div class="form-group mb-3">
          <label class="mb-2">Issue Description <span class="text-danger">*</span></label>

          <textarea class="form-control" rows="4" v-model="note" placeholder="Describe the issue..."
            @input="noteError = ''"></textarea>
          <small v-if="noteError" class="field-error">{{ noteError }}</small>
        </div>

        <div class="form-group mb-3">
          <label class="mb-2">Attachments</label>

          <div class="upload-box" @click="triggerFileInput">
            <button type="button" class="upload-btn">Upload Files</button>

            <span class="file-names" :class="{ empty: !files.length }">
              {{ files.length ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : "No file chosen" }}
            </span>

            <input ref="fileInput" type="file" class="d-none" multiple @change="handleFile"
              accept=".pdf,.png,.jpg,.jpeg" />
          </div>

          <div class="upload-info">
            Upload supporting documents (PDF, JPG, PNG). Max 5MB.
          </div>

          <div v-if="files.length" class="attachment-list mt-3">
            <div v-for="(file, index) in files" :key="file.id" class="attachment-card">
              <button type="button" class="attachment-preview-trigger" @click="previewFile(file)">
                <span class="attachment-icon" :class="{ 'is-pdf': isPdf(file) }">
                  <i :class="isPdf(file) ? 'far fa-file-pdf' : 'far fa-image'"></i>
                </span>

                <span class="attachment-meta">
                  <span class="attachment-name" :title="file.name">{{ file.name }}</span>
                  <span class="attachment-size">{{ formatFileSize(file.size) }}</span>
                </span>
              </button>

              <div class="attachment-actions">
                <button type="button" class="attachment-action-btn" @click="previewFile(file)" title="Preview"
                  aria-label="Preview attachment">
                  <i class="fas fa-eye"></i>
                </button>
                <button type="button" class="attachment-remove-btn" @click="removeFile(index)" title="Remove"
                  aria-label="Remove attachment">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-actions mt-4">
          <button class="btn btn-secondary fs-14" @click="closeModal">
            Cancel
          </button>

          <button class="btn btn-linear-primary fs-14" @click="saveNote" :disabled="isLoading">
            {{ isLoading ? "Submitting..." : "Submit Issue" }}
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="previewModalVisible" size="xl" centered hide-footer hide-header-close
      modal-class="attachment-preview-modal" content-class="attachment-preview-content"
      body-class="attachment-preview-body">
      <template #modal-header>
        <div class="attachment-preview-header">
          <b-button variant="close-btn" class="btn-container" @click="closePreviewModal">
            <i class="fas fa-times"></i>
          </b-button>
        </div>
      </template>

      <div v-if="previewFileItem" class="attachment-preview-shell">
        <img v-if="isImage(previewFileItem)" :src="previewFileItem.previewUrl" :alt="previewFileItem.name"
          class="attachment-preview-image" />

        <iframe v-else-if="isPdf(previewFileItem)" :src="previewFileItem.previewUrl" class="attachment-preview-pdf"
          frameborder="0" :title="previewFileItem.name"></iframe>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Helper from "@/resource/Helper";
import Api from "@/resource/Api";

export default {
  props: {
    isOpen: Boolean,
    row: { type: Object, default: () => ({}) },
    issueTypes: { type: Array, default: () => [] },
  },

  data() {
    return {
      modalVisible: this.isOpen,
      note: "",
      noteError: "",
      status: "",
      member: "",
      plan: "",
      markError: "",
      memberError: "",
      planError: "",
      files: [],
      previewModalVisible: false,
      previewFileItem: null,
      isLoading: false,
      isLoadingMembers: false,
      members: [],
      plans: [],
      memberSearchTimeout: null,
      isLoadingPlans: false,
      planSearchTimeout: null,
    };
  },

  created() {
    this.fetchMembers();
    this.fetchPlans();
  },

  watch: {
    isOpen(val) {
      this.modalVisible = val;
      if (val) {
        this.fetchMembers();
        this.fetchPlans();
      } else {
        this.resetForm();
      }
    },
    modalVisible(val) {
      if (!val) this.$emit("close");
    },
    previewModalVisible(val) {
      if (!val) {
        this.previewFileItem = null;
      }
    },
  },

  methods: {
    getMemberValue(member) {
      if (!member || typeof member !== "object") return member;
      return member.member_id;
    },

    getMemberLabel(member) {
      if (!member || typeof member !== "object") return member;
      return `${member.first_name || ""} ${member.last_name || ""} - ${member.policy_id || ""}`.trim();
    },

    getPlanValue(plan) {
      if (!plan || typeof plan !== "object") return plan;
      return plan.pid;
    },

    getPlanLabel(plan) {
      if (!plan || typeof plan !== "object") return plan;
      return plan.web_display_name || "N/A";
    },

    async fetchMembers(query = "") {
      this.isLoadingMembers = true;
      try {
        let url = "v2/downline-members";
        if (query) {
          url += `?q=${encodeURIComponent(query)}`;
        }
        const response = await Api.get(url);

        const resData = response?.data?.records;
        if (resData && resData.length) {
          this.members = resData;
        } else {
          this.members = [];
        }
      } catch (error) {
        this.members = [];
      } finally {
        this.isLoadingMembers = false;
      }
    },

    onMemberSearch(query) {
      if (!query) {
        if (!this.member) {
          this.members = [];
          this.fetchMembers();
        }
        return;
      }
      if (this.memberSearchTimeout) {
        clearTimeout(this.memberSearchTimeout);
      }
      this.memberSearchTimeout = setTimeout(() => {
        this.fetchMembers(query);
      }, 350);
    },

    async fetchPlans(query = "") {
      this.isLoadingPlans = true;
      try {
        let url = `v2/iha-plans`;
        if (query) {
          url += `?q=${encodeURIComponent(query)}`;
        }
        const response = await Api.get(url);
        this.plans = Array.isArray(response?.data?.records)
          ? response.data.records
          : [];
      } catch (error) {
        this.plans = [];
      } finally {
        this.isLoadingPlans = false;
      }
    },

    onPlanSearch(query) {
      if (!query) {
        if (!this.plan) {
          this.plans = [];
          this.fetchPlans();
        }
        return;
      }
      if (this.planSearchTimeout) {
        clearTimeout(this.planSearchTimeout);
      }
      this.planSearchTimeout = setTimeout(() => {
        this.fetchPlans(query);
      }, 350);
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFile(e) {
      const selectedFiles = Array.from(e.target.files || []);
      const allowedTypes = ["pdf", "png", "jpg", "jpeg"];

      selectedFiles.forEach((file) => {
        const ext = file.name.split(".").pop().toLowerCase();

        if (!allowedTypes.includes(ext)) {
          this.$toast.error("Unsupported file type");
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          this.$toast.error("File exceeds 5MB limit");
          return;
        }

        const duplicateFile = this.files.some((existingFile) => {
          return existingFile.name === file.name &&
            existingFile.size === file.size &&
            existingFile.lastModified === file.lastModified;
        });

        if (duplicateFile) {
          return;
        }

        this.files.push({
          id: `${file.name}-${file.size}-${file.lastModified}`,
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          rawFile: file,
          previewUrl: URL.createObjectURL(file),
        });
      });

      e.target.value = "";
    },

    formatFileSize(size) {
      if (!size) {
        return "0 KB";
      }

      const sizeInKb = size / 1024;
      if (sizeInKb < 1024) {
        return `${sizeInKb.toFixed(sizeInKb >= 100 ? 0 : 1)} KB`;
      }

      return `${(sizeInKb / 1024).toFixed(1)} MB`;
    },

    isPdf(file) {
      return file.name.toLowerCase().endsWith(".pdf");
    },

    isImage(file) {
      return [".png", ".jpg", ".jpeg"].some((ext) => file.name.toLowerCase().endsWith(ext));
    },

    previewFile(file) {
      this.previewFileItem = file;
      this.previewModalVisible = true;
    },

    closePreviewModal() {
      this.previewModalVisible = false;
    },

    removeFile(index) {
      const [removedFile] = this.files.splice(index, 1);

      if (removedFile && removedFile.previewUrl) {
        URL.revokeObjectURL(removedFile.previewUrl);
      }

      if (this.previewFileItem && removedFile && this.previewFileItem.id === removedFile.id) {
        this.closePreviewModal();
      }
    },

    resetForm() {
      this.note = "";
      this.noteError = "";
      this.status = "";
      this.member = "";
      this.plan = "";
      this.markError = "";
      this.isLoading = false;
      this.previewModalVisible = false;
      this.previewFileItem = null;

      this.files.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      this.files = [];

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    validateFormFields() {
      if (!this.status) {
        this.markError = "Please select an issue type";
        return false;
      }
      if (!this.member) {
        this.memberError = "Please select a member";
        return false;
      }
      if (!this.plan) {
        this.planError = "Please select a plan";
        return false;
      }
      if (!this.note.trim()) {
        this.noteError = "Description is required";
        return false;
      }
      return true;
    },

    async saveNote() {

      if(!this.validateFormFields()) return;

      const agent = this.$store.getters.rep;
      const nameParts = agent.name.trim().split(" ");
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(" ");

      const formData = new FormData();
      formData.append("first_name", firstName);
      formData.append("last_name", lastName);
      formData.append("email", agent.email);
      formData.append("message", this.note);
      formData.append("agent_id", agent.id);
      formData.append("type", this.status);
      formData.append("member_id", this.getMemberValue(this.member));
      formData.append("plan_id", this.getPlanValue(this.plan));
      formData.append("phone", this.member.phone || '');

      this.files.forEach((file, index) => {
        formData.append(`issue_document[${index}]`, file.rawFile);
      });

      this.isLoading = true;
      try {
        const apiUrl = `${process.env.VUE_APP_TICKETING_URL_API}v2/agent/create-iha-ticket`;
        const response = await axios.post(
          apiUrl,
          formData, {
          headers: {
            'X-Ticketing-Token': Helper.ticketingToken(),
          },
        }
        );

        if (response.status !== 200) {
          throw new Error("Failed to submit issue");
        }
        this.$toast.success(
          "Your issue has been successfully submitted to IHA Admin."
        );
        this.$emit("created");
        this.closeModal();

      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message || "Failed to submit issue"
        );
      } finally {
        this.isLoading = false;
      }
    },

    closeModal() {
      this.resetForm();
      this.modalVisible = false;
    },
  },
};
</script>
<style>
.iha-modal .modal-header {
  padding: 6px 10px;
}
.issue-modal-card .multiselect{
  width: 100% !important;
  margin-left: 0 !important;
}
.issue-modal-card .multiselect__tags{
  min-height: 40px !important;
}
.issue-modal-card .custom-select{
      padding: 9px 8px !important;
}

</style>

<style scoped>
.modal-title {
  font-weight: 600;
  margin-bottom: 20px;
}

.issue-info {
  background: rgba(243, 244, 246, 0.64);
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 15px;
  border: 1px solid rgba(229, 231, 235, 1);
}

.section-label {
  font-size: 15px;
  font-weight: 500;
  color: #717171;
}

.issue-user {
  display: flex;
  gap: 10px;
}

.issue-name-intial {
  background: #e5f7fa;
  padding: 6px 10px;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  font-weight: 600;
  color: #00ccff;
  font-size: 16px;
}

.issue-details .issue-name,
.issue-details .issue-plan {
  gap: 4px;
  font-size: 14px;
  color: #323c47;
  font-weight: 500;
}

.issue-name .icon-wrap,
.issue-plan .icon-wrap {
  color: #717171;
  font-size: 14px;
  width: 14px;
  height: 14px;
}

.issue-plan {
  font-size: 12px;
  color: #888;
}

.issue-modal-card .form-group label {
  color: #5e5e5e;
  font-weight: 500;
}

.form-control {
  font-size: 14px;
}

.form-control:focus {
  box-shadow: none;
  outline: none;
  border: 1px solid #ced4da;
}

.dropdown-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  pointer-events: none;
  color: #555;
  font-size: 13px;
}

.upload-box {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px dashed #c9d4df;
  border-radius: 12px;
  padding: 10px 12px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(240, 247, 251, 0.92));
  cursor: pointer;
}

.upload-btn {
  background: #00ccff;
  border: none;
  color: white;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  white-space: nowrap;
}

.file-names {
  color: #323c47;
  font-size: 14px;
}

.file-names.empty {
  color: #8b97a3;
}

.upload-info {
  font-size: 12px;
  color: #888;
  margin-top: 8px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-linear-primary {
  border: none;
  background: transparent linear-gradient(180deg, #0cf, #1597b8 49%, #0567bc) 0 0 no-repeat padding-box;
  border-radius: 6px;
  color: #fff !important;
  padding: 0.5rem 1rem;
  outline: none;
}

.fs-14 {
  font-size: 14px;
}

textarea::placeholder {
  color: #aaa;
  opacity: 1;
}

input::placeholder {
  color: #aaa;
  opacity: 1;
}

.attachment-list {
  display: grid;
  gap: 0;
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.attachment-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  min-width: 0;
  background: transparent;
  border-bottom: 1px solid #e2e8f0;
}

.attachment-card:last-child {
  border-bottom: none;
}

.attachment-preview-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.attachment-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 204, 255, 0.12);
  color: #00a9d9;
  flex-shrink: 0;
  font-size: 14px;
}

.attachment-icon.is-pdf {
  background: rgba(239, 68, 68, 0.12);
  color: #d63b3b;
}

.attachment-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
}

.attachment-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attachment-size {
  flex-shrink: 0;
  font-size: 11px;
  color: #6b7280;
}

.attachment-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.attachment-action-btn,
.attachment-remove-btn {
  border: none;
  background: transparent;
  border-radius: 6px;
  height: 28px;
}

.attachment-action-btn {
  width: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0f5c78;
  font-weight: 600;
  font-size: 12px;
}

.attachment-remove-btn {
  width: 28px;
  color: #b42318;
}

.attachment-preview-header {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
}

.attachment-preview-content {
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.attachment-preview-body {
  padding: 18px;
}

.attachment-preview-shell {
  min-height: 72vh;
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(180deg, #f7fafc, #edf4f7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.attachment-preview-image {
  max-width: 100%;
  max-height: 72vh;
  object-fit: contain;
  border-radius: 10px;
}

.attachment-preview-pdf {
  width: 100%;
  height: 72vh;
  border: none;
  background: #fff;
}

@media (max-width: 576px) {

  .upload-box,
  .modal-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .attachment-card {
    align-items: flex-start;
  }

  .attachment-preview-trigger {
    width: 100%;
  }

  .attachment-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .attachment-actions {
    justify-content: flex-end;
  }

  .attachment-action-btn,
  .upload-btn {
    width: 100%;
  }

  .attachment-remove-btn {
    width: 28px;
  }
}
</style>
